import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  FormControl,
  InputLabel
} from '@material-ui/core';
import * as React from 'react';
import { useIntl } from 'react-intl';
import SelectItem from 'pages/Report/components/Select/components/SelectItem';
import {
  MonthlyReportRecipient,
  MonthlyReportRecipientActions
} from 'redux/ducks/monthlyReportRecipients/types';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'redux/rootReducer';
import {
  updateRecipient as updateRecipientAction,
  createRecipient as createRecipientAction
} from 'redux/ducks/monthlyReportRecipients';
import { connect } from 'react-redux';
import { SUPPORTED_LOCALES } from 'components/LanguageSwitcher';
import { makeStyles } from '@material-ui/core/styles';

export interface RecipientEditorDialogProps {
  recipient?: MonthlyReportRecipient;
  mode: 'create' | 'edit';
  error?: boolean;
  onClose: () => void;
}

type DraftRecipient = Omit<MonthlyReportRecipient, 'id' | 'accountId' | 'userId'>;
type DraftRecipientField = 'language' | 'email';
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type DraftState = {
  recipient: DraftRecipient;
  errors: Record<DraftRecipientField, boolean>;
};

type OwnProps = DispatchProps & RecipientEditorDialogProps;

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  }
});

const EditorDialog: React.FC<OwnProps> = ({
  recipient,
  mode,
  onClose,
  updateRecipient,
  createRecipient
}) => {
  const intl = useIntl();
  const styles = useStyles();

  const [draft, setDraft] = React.useState<DraftState>({
    recipient: recipient ?? { language: 'en', email: '' },
    errors: { language: false, email: false }
  });
  const [pending, setPending] = React.useState(false);

  const title = intl.formatMessage({
    id: mode === 'edit' ? 'recipientEditor.editTitle' : 'recipientEditor.createTitle'
  });

  const formIsInvalid = Object.values(draft.errors).some((value) => value);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formIsInvalid) {
      return;
    }

    setPending(true);

    try {
      if (mode === 'create') {
        await createRecipient(draft.recipient);
        return;
      }

      if (!recipient) {
        return;
      }

      await updateRecipient(recipient.id, draft.recipient);
    } finally {
      setPending(false);
    }
  };

  const handleChange = <TKey extends keyof MonthlyReportRecipient>(
    key: TKey,
    value: MonthlyReportRecipient[TKey]
  ) => {
    setDraft((prev) => ({ ...prev, recipient: { ...prev.recipient, [key]: value } }));
  };

  const handleFieldValidation = (key: DraftRecipientField) => {
    const isEmpty = !draft.recipient[key];
    setDraft((prev) => ({ ...prev, errors: { ...prev.errors, [key]: isEmpty } }));
  };

  return (
    <Dialog open maxWidth='sm' fullWidth className='recipientDialog' onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <form
          id='recipient-editor'
          onSubmit={(event) => {
            void handleSave(event);
          }}
          className={styles.form}
        >
          <TextField
            label={intl.formatMessage({ id: 'emailAddress' })}
            value={draft.recipient.email}
            required
            autoFocus
            type='email'
            name='email'
            error={draft.errors.email}
            fullWidth
            onBlur={() => handleFieldValidation('email')}
            onChange={(e) => handleChange('email', e.target.value)}
          />
          <FormControl>
            <InputLabel htmlFor='language'>Language</InputLabel>
            <Select
              inputProps={{ id: 'language', name: 'language' }}
              label={intl.formatMessage({ id: 'language' })}
              value={draft.recipient.language}
              required
              error={draft.errors.language}
              fullWidth
              onBlur={() => handleFieldValidation('language')}
              onChange={(e) => handleChange('language', e.target.value as string)}
            >
              {SUPPORTED_LOCALES.map((locale) => (
                <SelectItem key={locale} value={locale}>
                  {intl.formatMessage({ id: `languages.${locale}` })}
                </SelectItem>
              ))}
            </Select>
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {intl.formatMessage({ id: 'base.cancel' })}
        </Button>
        <Button
          disabled={formIsInvalid || pending}
          variant='contained'
          form='recipient-editor'
          type='submit'
          color='primary'
        >
          {intl.formatMessage({ id: 'base.save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapDispatchToProps = (
  dispatch?: ThunkDispatch<RootState, void, MonthlyReportRecipientActions>
) => ({
  createRecipient: (recipient: DraftRecipient) => dispatch(createRecipientAction(recipient)),
  updateRecipient: (id: MonthlyReportRecipient['id'], recipient: Partial<MonthlyReportRecipient>) =>
    dispatch(updateRecipientAction(id, recipient))
});

export const RecipientEditorDialog = connect<unknown, DispatchProps, RecipientEditorDialogProps>(
  null,
  mapDispatchToProps
)(EditorDialog);
